import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import classNames from 'classnames';
import Title from '@/components/school-management/TitleStyles/title';
import Button from '@/components/school-management/Button/Button';
import SearchBar from '@/components/school-management/SearchBar/SearchBar';
import Tag from '@/components/school-management/Tag/Tag';
import styles from './SchoolsListPage.module.scss';
import GenericTable, {
  TableColumn,
} from '@/components/school-management/GenericTable/GenericTable';
import {
  BimloaFilter,
  FilterField,
  FilterValue,
  DateFilterValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import { School, schoolsTableData, getUniqueOptions } from '@/admin/config/listConfig';
import PageHeader from '@/components/school-management/GenericTable/PageHeader/PageHeader';
import StatsCardGroup from '@/components/school-management/StatsCard/StatsCardGroup';

const SchoolsListPage = () => {
  const { t } = useAdminTranslation();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, FilterValue>>({
    type: 'All',
    status: 'All',
    admin: 'All',
    adminEmail: 'All',
  });

  // Build filter fields
  const filterFields: FilterField[] = useMemo(
    () => [
      {
        key: 'type',
        label: t('schoolsListPage.filters.schoolType'),
        options: getUniqueOptions(schoolsTableData, 'type'),
      },
      {
        key: 'status',
        label: t('schoolsListPage.filters.status'),
        options: getUniqueOptions(schoolsTableData, 'status'),
      },
      {
        key: 'admin',
        label: t('schoolsListPage.filters.schoolAdmin'),
        options: getUniqueOptions(schoolsTableData, 'admin'),
      },
      {
        key: 'adminEmail',
        label: t('schoolsListPage.filters.adminEmail'),
        options: getUniqueOptions(schoolsTableData, 'adminEmail'),
      },
    ],
    [t]
  );

  // TODO: Replace with API filtering logic when ready
  const filteredSchools = useMemo(() => {
    return schoolsTableData.filter(
      school =>
        (school.name.toLowerCase().includes(search.toLowerCase()) ||
          school.type.toLowerCase().includes(search.toLowerCase())) &&
        Object.entries(filters).every(([key, value]) => {
          if (typeof value === 'string') {
            if (value === 'All') return true;
            return String((school as any)[key]) === value;
          } else if (Array.isArray(value)) {
            if (value.length === 0) return true;
            return (value as string[]).includes(String((school as any)[key]));
          } else if (value && typeof value === 'object' && 'mode' in value) {
            return value.mode === 'none';
          }
          return true;
        })
    );
  }, [search, filters]);

  // Table columns
  const columns: TableColumn<School>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('schoolsListPage.columns.schoolName'),
        width: '2.2fr',
        sortable: true,
        render: (value, row) => (
          <div>
            <div className={styles.schoolName}>{row.name}</div>
            <div className={styles.schoolType}>{row.type}</div>
          </div>
        ),
        xlsxRender: (value, row) => `${row.name} \r\n${row.type}`,
      },
      {
        key: 'status',
        label: t('schoolsListPage.columns.status'),
        width: '1.5fr',
        sortable: true,
        headerAlign: 'center',
        render: value => (
          <div className={styles.centeredCell}>
            <Tag type={value === 'active' ? 'ACTIVE' : 'INACTIVE'} />
          </div>
        ),
        xlsxRender: (value, row) => (value === 'active' ? 'ACTIVE' : 'INACTIVE'),
      },
      {
        key: 'admin',
        label: t('schoolsListPage.columns.schoolAdmin'),
        width: '1.5fr',
        sortable: true,
        headerAlign: 'center',
        render: value => <div className={styles.centeredCell}>{value}</div>,
        xlsxRender: (value, row) => `${value}`,
      },
      {
        key: 'adminEmail',
        label: t('schoolsListPage.columns.schoolAdminEmail'),
        width: '2fr',
        sortable: true,
        headerAlign: 'center',
        render: value => <div className={styles.centeredCell}>{value}</div>,
        xlsxRender: (value, row) => `${value}`,
      },
      {
        key: 'registeredUsers',
        label: t('schoolsListPage.columns.registeredUsers'),
        width: '1.1fr',
        sortable: true,
        render: value => `${value} (${Math.round((value / 1000) * 100)}%)`,
        xlsxRender: (value, row) => `${value} (${Math.round((value / 1000) * 100)}%)`,
      },
      {
        key: 'activeUsers',
        label: t('schoolsListPage.columns.activeUsers'),
        width: '1.1fr',
        sortable: true,
        render: value => `${value} (${Math.round((value / 100) * 100)}%)`,
        xlsxRender: (value, row) => `${value} (${Math.round((value / 100) * 100)}%)`,
      },
      {
        key: 'actions',
        label: '',
        width: '0.5fr',
        render: (_, row) => (
          <Button
            secondary={true}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = `/schools/${row.name.toLowerCase().replace(/\s+/g, '-')}?tab=general`;
            }}
          >
            <AppIcon icon='arrowRight' />
          </Button>
        ),
      },
    ],
    [t]
  );

  return (
    <div className={classNames(styles.wrapper)}>
      <PageHeader
        title={t('schoolsListPage.title')}
        filterFields={filterFields}
        filters={filters}
        setFilters={setFilters}
        linkTo='/add-school'
        buttonText={t('schoolsListPage.newSchool') || ''}
      />
      <div className={styles.schoolsDetailsCard}>
        <GenericTable<School>
          levelHint={t('schoolsListPage.allLevelsHint') || ''}
          columns={columns}
          data={filteredSchools}
          rowKey={school => school.name}
          detailsPath={school =>
            `/schools/${school.name.toLowerCase().replace(/\s+/g, '-')}?tab=general`
          }
          pageSize={8}
          className={classNames(styles.tableWrapper)}
          variant='compact'
          title={t('schoolsListPage.title') || ''}
          emptyStateAction={
            <a href='/CreateSchoolPage' style={{ color: '#1463b0', fontWeight: 500 }}>
              {t('schoolsListPage.createNewSchool')}
            </a>
          }
          hasData={schoolsTableData.length > 0}
          onDownload={() => console.log('Clicked to download schools CSV')}
          downloadFileName={t('schoolsListPage.title') || ''}
        />
      </div>
    </div>
  );
};

export default SchoolsListPage;
